/**
 * Timeline playback and scenario controls.
 * @module HybridSystemIntegration/components/TimelineController
 */

import React from 'react';
import { Button, Card, Select, Slider, Space, Typography } from 'antd';
import { PauseCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { SCENARIOS } from '../constants';
import styles from '../HybridSystemIntegration.module.css';

const { Text, Title } = Typography;

/**
 * Timeline and scenario controller.
 * @param {Object} props - Component props.
 * @returns {JSX.Element} Controller card.
 */
export function TimelineController({
  settings,
  isPlaying,
  scenarioKey,
  onApplyScenario,
  onPlayingChange,
  onUpdateSetting,
}) {
  const { t } = useTranslation();
  const scenarioOptions = Object.values(SCENARIOS).map((scenario) => ({
    label: t(scenario.translationKey),
    value: scenario.key,
  }));

  return (
    <Card className={styles.timelineCard}>
      <div className={styles.timelineHeader}>
        <div>
          <Title level={5}>{t('hybridSystemTool.timeline.title')}</Title>
          <Text type="secondary">{t('hybridSystemTool.timeline.subtitle')}</Text>
        </div>
        <Button
          icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
          onClick={() => onPlayingChange(!isPlaying)}
        >
          {isPlaying ? t('hybridSystemTool.actions.pause') : t('hybridSystemTool.actions.play')}
        </Button>
      </div>

      <div className={styles.timelineSliderRow}>
        <Text strong>{settings.hourOfDay}:00</Text>
        <Slider
          min={0}
          max={23}
          value={settings.hourOfDay}
          marks={{ 0: '0', 6: '6', 12: '12', 18: '18', 23: '23' }}
          onChange={(value) => onUpdateSetting('hourOfDay', value)}
        />
      </div>

      <Space direction="vertical" size="small" className={styles.fullWidth}>
        <Text strong>{t('hybridSystemTool.timeline.scenarioChallenges')}</Text>
        <Select
          value={scenarioKey}
          options={scenarioOptions}
          onChange={onApplyScenario}
        />
      </Space>
    </Card>
  );
}

export default TimelineController;
